import { Upload, type FormInstance, Modal, notification } from 'antd';
import type { UploadFile, UploadProps } from 'antd';
import { DeleteOutlined, FilePdfOutlined, InboxOutlined } from '@ant-design/icons';
import { post } from '@/common/services/httpClient';
import type { AxiosProgressEvent } from 'axios';
// import { useRouter } from "@tanstack/react-router";
import { useAppMutation } from '@/common/hooks/useAppMutation';
import type { FormField } from './DynamicFormTypes';
type FileUploadRequestField = FormField;
import { useEffect, useState } from 'react';
import { useTranslation } from "react-i18next";
import React from 'react';

interface FileUploadFieldProps {
    field: FileUploadRequestField;
    commonProps: object;
    value?: UploadFile[] | string;
    onChange?: (value: UploadFile[]) => void;
    form: FormInstance;
    onUploadingChange?: (isUploading: boolean) => void; // <--- add this

}
interface UploadApiResponse<T = { path: string }> {
    data: T | null;
    paging?: unknown;
    statusCode: number;
    isError: boolean;
    title?: string;
    developerMessage?: string;
    message?: string;
    messageArgs?: unknown;
    userType?: string | null;
}
const FileUploadRequestField = ({ field, commonProps, value, onChange, form, onUploadingChange }: FileUploadFieldProps) => {
    const { Dragger } = Upload;
    const [fileList, setFileList] = useState<UploadFile[]>([]);
    const VITE_API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
    const { t } = useTranslation();
    const [uploadingFiles, setUploadingFiles] = useState<Record<string, boolean>>({});
    const [hover, setHover] = React.useState(false); // hover state


    // const { state: routerState } = useRouter().state.location;
    // const record = (routerState as { record?: Record<string, unknown> })?.record;
    // const claimId = record?.id?.toString();

    const deleteClaimFileMutation = useAppMutation<{ id?: number; fileName: string }, unknown>(
        async (variables) => {
            return post('Claim/DeleteClaimFile', variables);
        },
        {
            onSuccess: (response: any) => {
                if (response.isError) {
                    // ❌ Show server message (e.g. File not found)
                    notification.error({
                        message: response.title || t('Deletion Failed'),
                        description: response.message || t('An unknown error occurred while deleting the file'),
                    });
                } else {
                    // ✅ Show success message from API (if present)
                    notification.success({
                        message: response.title || t('File Deleted'),
                        description: response.message || t('The file has been successfully deleted'),
                    });
                }
            },
            onError: (error) => {
                // ⚠️ Handles unexpected network or code-level errors
                notification.error({
                    message: t('Network Error'),
                    description: (error as Error).message || t('A network or unexpected error occurred.'),
                });
            },
        }
    );

    useEffect(() => {
        if (typeof onUploadingChange === "function") {
            const anyUploading = Object.keys(uploadingFiles).length > 0;
            onUploadingChange(anyUploading);
        }
    }, [uploadingFiles]);


    useEffect(() => {
        //console.log("value",value);
        if (value) {
            if (typeof value === 'string') {
                const newFileList = [
                    {
                        uid: '-1',
                        name: value.substring(value.lastIndexOf('/') + 1),
                        status: 'done',
                        url: `${value}`,
                    },
                ];
                setFileList(newFileList as UploadFile[]);
                //console.log("newFileList",newFileList);
            } else {
                setFileList(value as UploadFile[]);
            }
        } else {
            setFileList([]);
        }
    }, [value, VITE_API_BASE_URL]);
    const handleFileChange: UploadProps['onChange'] = (info) => {
        let newFileList = [...info.fileList];

        // 1. Limit the number of uploaded files
        if (field.maxCount) {
            newFileList = newFileList.slice(-field.maxCount);
        }

        // 2. Read from response and show file link
        newFileList = newFileList.map((file) => {
            if (file.response) {
                // Component will show file.url as link
                const responseValue = field.responseValueKey ? file.response[field.responseValueKey] : file.response.url;
                file.url = responseValue;
                file.name = responseValue; // Also update the name for display
            }
            return file;
        });

        onChange?.(newFileList);
        setFileList(newFileList);
        form.setFieldsValue({ [field.name]: newFileList });
    };

    const handleRemove = (file: UploadFile) => {
        Modal.confirm({
            title: 'Confirm Delete',
            content: t(`Are you sure you want to delete?`),
            okText: t('Delete'),
            cancelText: t('Cancel'),
            onOk: async () => {
                await deleteClaimFileMutation.mutateAsync({ id: file.id, fileName: file.name } as any);
                const newFileList = fileList.filter((item) => item.uid !== file.uid);
                onChange?.(newFileList);
                setFileList(newFileList);
                if (field.updateFieldName) {
                    form.setFieldsValue({ [field.updateFieldName]: newFileList.length > 0 ? newFileList[0].url : null });
                }

            },
            onCancel: () => {

            },
        });
        return false; // Return false to prevent immediate removal by Ant Design's Upload component
    };

    const customRequest: UploadProps['customRequest'] = async (options) => {

        const { onSuccess, onError, file, onProgress } = options;
        const uploadFile = file as UploadFile; // <--- typecast here
        setUploadingFiles(prev => ({ ...prev, [uploadFile.uid]: true }));

        const formData = new FormData();
        const fileKey = field.fileKey || 'file';
        formData.append(fileKey, file as Blob);


        try {
            const response = await post<UploadApiResponse>(field.uploadUrl!, formData, {
                onUploadProgress: (event: AxiosProgressEvent) => {
                    if (event.total) {
                        const percent = Math.floor((event.loaded / event.total) * 100);
                        onProgress?.({ percent });
                    }
                },
            });
            if (response.isError) {
                notification.error({
                    message: response.title || t("Upload Failed"),
                    description: response.message || t("An unknown error occurred while uploading"),
                });
                onError?.(new Error(response.message || "Upload failed"));
                return;
            }
            if (field.updateFieldName && response.data?.path) {
                form.setFieldsValue({ [field.updateFieldName]: response.data.path });
            }
            onSuccess?.(response.data);


        } catch (error) {
            onError?.(error as Error);
        }
        finally {
            // mark file as done
            setUploadingFiles(prev => {
                const newState = { ...prev };
                delete newState[uploadFile.uid];
                return newState;
            });
        }
    };

    const beforeUpload: UploadProps['beforeUpload'] = (file) => {

        // Example: Check file size (e.g., max 50MB)
        const isLt2M = file.size / 1024 / 1024 < 50;
        if (!isLt2M) {
            // message.error('File must be smaller than 50MB!');
            console.error('File must be smaller than 50MB');
            notification.error({
                message: t('Upload Error'),
                description: t('File must be smaller than 50MB'),
            });
            return Upload.LIST_IGNORE; // Prevent upload
        }
        return isLt2M;
    };

    const props: UploadProps = {
        ...commonProps,
        name: field.name,
        multiple: (field.maxCount ?? 1) > 1,
        accept: field.accept,
        fileList: fileList as UploadFile[],
        maxCount: field.maxCount,
        customRequest: customRequest,
        onChange: handleFileChange,
        onRemove: handleRemove,
        beforeUpload: beforeUpload,
        listType: "picture-card",

        itemRender: (originNode, file) => {
            const isPdf = file.name?.toLowerCase().endsWith(".pdf");

            if (isPdf) {
                return (
                    <div
                        className="pdf-item"
                        style={{
                            width: 113,
                            height: 113,
                            borderRadius: 8,
                            padding: 8,
                            position: "relative",
                            overflow: "hidden",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            border: "1px solid #d9d9d9",
                            cursor: "pointer",
                            backgroundColor: "#fafafa",
                        }}
                        onMouseEnter={() => setHover(true)}
                        onMouseLeave={() => setHover(false)}
                    >
                        {/* PDF Icon */}
                        <FilePdfOutlined style={{ fontSize: 40, color: "red", zIndex: 1 }} />

                        {/* Overlay */}
                        <div
                            className="overlay"
                            style={{
                                position: "absolute",
                                top: 8,
                                left: 8,
                                right: 8,
                                bottom: 8,
                                backgroundColor: "rgba(0,0,0,0.45)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                opacity: hover ? 1 : 0,
                                transition: "opacity 0.2s",
                                zIndex: 2,
                            }}
                        >
                            <DeleteOutlined
                                style={{ fontSize: 16, color: "white", cursor: "pointer", padding: 8 }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleRemove(file);
                                }}
                            />
                        </div>

                        
                    </div>
                );
            }

            return React.cloneElement(originNode as React.ReactElement, {
                onClick: (e: any) => e.preventDefault(),
            });
        },

        showUploadList: {
            showRemoveIcon: true, // Show the delete icon
            showPreviewIcon: false, // Show the view button
            showDownloadIcon: false, // Hide the download icon if not needed
        },
    };

    return (
        <Dragger {...props}>
            <p className="ant-upload-drag-icon">
                <InboxOutlined />
            </p>
            <p className="ant-upload-text">{t("Click or drag file to this area to upload")}</p>
            <p className="ant-upload-hint">
                {t("Support for a single or bulk upload. Strictly prohibited from uploading company data or other banned files")}
            </p>
        </Dragger>
    );
};

export default FileUploadRequestField;